import React, { useState, useEffect } from 'react';
import { ExtensionSettings as ExtensionSettingsType, ExtensionSection } from '../types';
import { getExtensionSettings, updateExtensionSetting, saveChatbotSettings } from '../lib/extensionSettings';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Voice } from '../lib/voices';

interface ExtensionSettingsProps {
  extensionId: string;
  settings: ExtensionSettingsType;
}

const ExtensionSettings: React.FC<ExtensionSettingsProps> = ({ extensionId, settings }) => {
  const { user } = useAuth();
  const currentSettings = settings[extensionId];
  const [ttsOverlayUrl, setTtsOverlayUrl] = useState<string>('');
  const [goalsOverlayUrl, setGoalsOverlayUrl] = useState<string>('');
  const [rumbleChatMonitorUrl, setRumbleChatMonitorUrl] = useState<string>('');
  const [socialMediaRotatorUrl, setSocialMediaRotatorUrl] = useState<string>('');
  const [isLoadingUrl, setIsLoadingUrl] = useState<boolean>(false);
  const [urlCopied, setUrlCopied] = useState<boolean>(false);
  const [goalsUrlCopied, setGoalsUrlCopied] = useState<boolean>(false);
  const [rumbleChatUrlCopied, setRumbleChatUrlCopied] = useState<boolean>(false);
  const [socialUrlCopied, setSocialUrlCopied] = useState<boolean>(false);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState<boolean>(false);
  const [ttsIntegrationEnabled, setTtsIntegrationEnabled] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<{ [key: string]: any }>(() => {
    const initialValues: { [key: string]: any } = {};
    if (currentSettings?.fields) {
      currentSettings.fields.forEach(field => {
        const defaultValue = field.default || (field.type === 'user-list' || field.type === 'social-media-list' ? [] : '');
        initialValues[field.id] = field.id === 'bot-personality' && typeof defaultValue === 'string'
          ? defaultValue.replace(/\\n/g, '\n')
          : defaultValue;
      });
    } else if (currentSettings?.sections) {
      currentSettings.sections.forEach(section => {
        section.fields.forEach(field => {
          const defaultValue = field.default || (field.type === 'user-list' || field.type === 'social-media-list' ? [] : '');
          initialValues[field.id] = field.id === 'bot-personality' && typeof defaultValue === 'string'
            ? defaultValue.replace(/\\n/g, '\n')
            : defaultValue;
        });
      });
    }
    // Ensure selected-voice has a default value for chat-bot
    if (!initialValues['selected-voice'] && (extensionId === 'chat-bot' || extensionId === 'chatbot')) {
      initialValues['selected-voice'] = '54e3a85ac9594ffa83264b8a494b901b';
    }
    return initialValues;
  });

  const [newUserInputs, setNewUserInputs] = useState<{ [key: string]: string }>({});
  const [newSocialInputs, setNewSocialInputs] = useState<{ [key: string]: { platform: string; handle: string } }>({});
  const [ttsExpanded, setTtsExpanded] = useState<boolean>(() => {
    // Initialize TTS expanded state based on the enable-tts toggle value
    const ttsSection = currentSettings?.sections?.find(section =>
      section.title.toLowerCase().includes('tts')
    );
    const ttsToggle = ttsSection?.fields.find(field => field.id === 'enable-tts');
    return Boolean(ttsToggle?.default || false);
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [testTtsMessage, setTestTtsMessage] = useState<string>('');
  const [isSendingTestTts, setIsSendingTestTts] = useState<boolean>(false);
  const [testTtsStatus, setTestTtsStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [rateLimitInfo, setRateLimitInfo] = useState<{ remaining: number; limit: number } | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState<boolean>(true);
  const [originalValues, setOriginalValues] = useState<{ [key: string]: any }>({});
  const [mentionWordSaveStatus, setMentionWordSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewKey, setPreviewKey] = useState<number>(0);
  const [previewBgLight, setPreviewBgLight] = useState<boolean>(false);

  // Load existing settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoadingSettings(true);
      try {
        const extensionSettings = await getExtensionSettings(extensionId);

        // Load bot_enabled, tts_enabled, and mention_word from dedicated columns
        const { data: rumbleSettings } = await supabase
          .from('user_rumble_settings')
          .select('bot_enabled, tts_enabled, mention_word')
          .eq('user_id', user?.id)
          .single();

        if (extensionSettings) {
          console.log('📥 Loading extension settings for:', extensionId);
          console.log('📥 Extension settings from DB:', extensionSettings);

          if (extensionId === 'chat-bot' || extensionId === 'chatbot') {
            const mentionWord = rumbleSettings?.mention_word || 'dork';
            setFormValues(prev => {
              const personalityValue = extensionSettings.personality || extensionSettings['bot-personality'] || prev['bot-personality'];
              return {
                ...prev,
                'bot-personality': typeof personalityValue === 'string' ? personalityValue.replace(/\\n/g, '\n') : personalityValue,
                'bot-enabled': rumbleSettings?.bot_enabled !== undefined ? rumbleSettings.bot_enabled : false,
                'response-mode': extensionSettings['response-mode'] || 'chat',
                'selected-voice': extensionSettings.voice_id || '54e3a85ac9594ffa83264b8a494b901b',
                'mention-word': mentionWord
              };
            });
            // Store original values for comparison
            setOriginalValues(prev => ({
              ...prev,
              'mention-word': mentionWord
            }));
          } else if (extensionId === 'tts-integration') {
            // For tts-integration, load from dedicated column
            setFormValues(prev => ({
              ...prev,
              'tts-enabled': rumbleSettings?.tts_enabled !== undefined ? rumbleSettings.tts_enabled : false
            }));
          } else {
            // For other extensions, load all saved settings
            console.log('📥 Loading settings for other extension');
            Object.keys(extensionSettings).forEach(key => {
              if (key !== 'enabled') {
                console.log(`📥 Setting ${key}:`, extensionSettings[key]);
                setFormValues(prev => ({ ...prev, [key]: extensionSettings[key] }));
              }
            });
          }
        } else {
          console.log('⚠️ No extension settings found for:', extensionId);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, [extensionId, user]);

  // Fetch voices from Supabase
  useEffect(() => {
    const fetchVoices = async () => {
      if (extensionId === 'chat-bot' || extensionId === 'chatbot' || extensionId === 'tts-integration') {
        setIsLoadingVoices(true);
        try {
          const { data, error } = await supabase
            .from('voices')
            .select('id, name, avatar, short_name')
            .order('name', { ascending: true });

          if (error) {
            console.error('Error fetching voices:', error);
          } else if (data) {
            setVoices(data);
          }
        } catch (error) {
          console.error('Failed to fetch voices:', error);
        } finally {
          setIsLoadingVoices(false);
        }
      }
    };

    fetchVoices();
  }, [extensionId]);

  // Check if tts-integration is enabled (for dynamic field options)
  useEffect(() => {
    const checkTtsIntegration = async () => {
      try {
        const ttsSettings = await getExtensionSettings('tts-integration');
        setTtsIntegrationEnabled(ttsSettings?.enabled || false);
      } catch (error) {
        console.error('Failed to check tts-integration status:', error);
        setTtsIntegrationEnabled(false);
      }
    };

    checkTtsIntegration();
  }, [extensionId, settings]);

  // Fetch TTS overlay URL for tts-integration extension
  useEffect(() => {
    const fetchTtsOverlayUrl = async () => {
      if (extensionId === 'tts-integration' && user) {
        setIsLoadingUrl(true);
        try {
          // Get user's settings_id and user_id from user_rumble_settings
          const { data, error } = await supabase
            .from('user_rumble_settings')
            .select('id, user_id')
            .eq('user_id', user.id)
            .single();

          if (error) {
            console.error('Error fetching user settings:', error);
            setTtsOverlayUrl('Error loading URL - please contact support');
          } else if (data) {
            // Construct the TTS overlay URL
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/tts-overlay?id=${data.id}&user=${data.user_id}`;
            setTtsOverlayUrl(url);
          } else {
            setTtsOverlayUrl('No settings found - please save your settings first');
          }
        } catch (error) {
          console.error('Failed to fetch TTS overlay URL:', error);
          setTtsOverlayUrl('Error loading URL');
        } finally {
          setIsLoadingUrl(false);
        }
      }
    };

    fetchTtsOverlayUrl();
  }, [extensionId, user]);

  // Fetch Goals overlay URL for goals-overlay extension
  useEffect(() => {
    const fetchGoalsOverlayUrl = async () => {
      if (extensionId === 'goals-overlay' && user) {
        try {
          // Construct the Goals overlay URL
          const baseUrl = window.location.origin;
          const url = `${baseUrl}/goals-overlay.html?user=${user.id}`;
          setGoalsOverlayUrl(url);
          // Also update the form value so it shows in the input
          setFormValues(prev => ({...prev, 'overlay-url': url}));
        } catch (error) {
          console.error('Failed to generate Goals overlay URL:', error);
          setGoalsOverlayUrl('Error generating URL');
        }
      }
    };

    fetchGoalsOverlayUrl();
  }, [extensionId, user]);

  // Fetch Rumble Chat Monitor overlay URL for rumble-chat-monitor extension
  useEffect(() => {
    const fetchRumbleChatMonitorUrl = async () => {
      if (extensionId === 'rumble-chat-monitor' && user) {
        try {
          const baseUrl = window.location.origin;
          const overlayType = formValues['overlay-type'] || 'full';
          const topChatters = formValues['top-chatters-count'] || 20;
          const stats = (formValues['display-stats'] || []).join(',');
          const bgColor = encodeURIComponent(formValues['bg-color'] || '#121418');
          const textColor = encodeURIComponent(formValues['text-color'] || '#ffffff');
          const accentColor = encodeURIComponent(formValues['accent-color'] || '#85c742');

          // Map overlay type to the correct HTML file
          const overlayFileMap: { [key: string]: string } = {
            'full': 'rumble-chat-monitor.html',
            'engagement': 'rumble-engagement-overlay.html',
            'viewers': 'rumble-viewers-overlay.html',
            'chatters': 'rumble-chatters-overlay.html',
            'combined': 'rumble-combined-overlay.html'
          };

          const overlayFile = overlayFileMap[overlayType] || 'rumble-chat-monitor.html';

          // Build URL based on overlay type
          let url = `${baseUrl}/${overlayFile}?user=${user.id}&bgColor=${bgColor}&textColor=${textColor}&accentColor=${accentColor}`;

          // Add additional params for full stats overlay
          if (overlayType === 'full') {
            url += `&topChatters=${topChatters}&stats=${stats}`;
          }

          setRumbleChatMonitorUrl(url);
        } catch (error) {
          console.error('Failed to generate Rumble Chat Monitor overlay URL:', error);
          setRumbleChatMonitorUrl('Error generating URL');
        }
      }
    };

    fetchRumbleChatMonitorUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionId, user, formValues['overlay-type'], formValues['top-chatters-count'], formValues['display-stats'], formValues['bg-color'], formValues['text-color'], formValues['accent-color']]);

  // Fetch Social Media Rotator overlay URL for social-media-rotator extension
  useEffect(() => {
    const fetchSocialMediaRotatorUrl = async () => {
      if (extensionId === 'social-media-rotator' && user) {
        try {
          const baseUrl = window.location.origin;
          const iconSize = formValues['icon-size'] || '50';
          const fontFamily = encodeURIComponent(formValues['font-family'] || '"Bebas Neue", sans-serif');
          const textSize = formValues['text-size'] || '25';
          const textColor = encodeURIComponent(formValues['text-color'] || '#ffffff');
          const bgColor = encodeURIComponent(formValues['bg-color'] || 'transparent');
          const animationStyle = formValues['animation-style'] || 'fade';
          const rotationSpeed = formValues['rotation-speed'] || '5';
          const animationDirection = formValues['animation-direction'] || 'down';
          const textShadow = encodeURIComponent(formValues['text-shadow'] || 'none');
          const socials = encodeURIComponent(JSON.stringify(formValues['social-media-links'] || []));

          const url = `${baseUrl}/social-media-rotator.html?user=${user.id}&iconSize=${iconSize}&fontFamily=${fontFamily}&textSize=${textSize}&textColor=${textColor}&bgColor=${bgColor}&animationStyle=${animationStyle}&rotationSpeed=${rotationSpeed}&animationDirection=${animationDirection}&textShadow=${textShadow}&socials=${socials}`;

          // Store clean URL without cache buster for copying
          setSocialMediaRotatorUrl(url);
          setFormValues(prev => ({...prev, 'overlay-url': url}));

          // Auto-refresh preview when URL changes
          setPreviewKey(prev => prev + 1);
        } catch (error) {
          console.error('Failed to generate Social Media Rotator overlay URL:', error);
          setSocialMediaRotatorUrl('Error generating URL');
        }
      }
    };

    fetchSocialMediaRotatorUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionId, user, formValues['icon-size'], formValues['font-family'], formValues['text-size'], formValues['text-color'], formValues['bg-color'], formValues['animation-style'], formValues['rotation-speed'], formValues['animation-direction'], formValues['text-shadow'], formValues['social-media-links']]);

  const handleInputChange = async (fieldId: string, value: any) => {
    setFormValues(prev => ({ ...prev, [fieldId]: value }));

    // Auto-save for social-media-rotator extension
    if (extensionId === 'social-media-rotator') {
      try {
        await updateExtensionSetting(extensionId, {
          ...formValues,
          [fieldId]: value,
          enabled: true
        });
      } catch (error) {
        console.error('Failed to auto-save social-media-rotator setting:', error);
      }
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('saving');

    try {
      if (extensionId === 'chat-bot' || extensionId === 'chatbot') {
        // Get the selected voice details
        const selectedVoiceId = formValues['selected-voice'] || '54e3a85ac9594ffa83264b8a494b901b';
        const selectedVoice = voices.find(v => v.id === selectedVoiceId);
        const voiceName = selectedVoice?.name || 'SpongeBob';

        await saveChatbotSettings(
          formValues['bot-personality'] || '',
          selectedVoiceId,
          voiceName,
          true // Extension is enabled when settings are saved
        );
      } else {
        // For other extensions, save all form values
        console.log('💾 Saving extension settings for:', extensionId);
        console.log('💾 Form values being saved:', formValues);
        await updateExtensionSetting(extensionId, {
          ...formValues,
          enabled: true // Keep extension activated
        });
        console.log('✅ Settings saved successfully');
      }

      setSaveStatus('success');

      // Refresh preview if goals-overlay
      if (extensionId === 'goals-overlay' && showPreview) {
        setTimeout(() => {
          setPreviewKey(prev => prev + 1);
        }, 500);
      }

      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMentionWord = async () => {
    setMentionWordSaveStatus('saving');

    try {
      const { error } = await supabase
        .from('user_rumble_settings')
        .update({ mention_word: formValues['mention-word'] })
        .eq('user_id', user?.id);

      if (error) throw error;

      // Update original value after successful save
      setOriginalValues(prev => ({
        ...prev,
        'mention-word': formValues['mention-word']
      }));

      setMentionWordSaveStatus('success');
      setTimeout(() => setMentionWordSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save mention word:', error);
      setMentionWordSaveStatus('error');
      setTimeout(() => setMentionWordSaveStatus('idle'), 3000);
    }
  };

  const handleAddUser = (fieldId: string, username: string) => {
    if (username.trim()) {
      const currentList = formValues[fieldId] || [];
      if (!currentList.includes(username.trim().toLowerCase())) {
        handleInputChange(fieldId, [...currentList, username.trim().toLowerCase()]);
      }
    }
  };

  const handleRemoveUser = (fieldId: string, username: string) => {
    const currentList = formValues[fieldId] || [];
    handleInputChange(fieldId, currentList.filter((user: string) => user !== username));
  };

  const handleRoleToggle = (fieldId: string, role: string) => {
    const currentRoles = formValues[fieldId] || [];
    if (currentRoles.includes(role)) {
      handleInputChange(fieldId, currentRoles.filter((r: string) => r !== role));
    } else {
      handleInputChange(fieldId, [...currentRoles, role]);
    }
  };

  const handleSendTestTts = async () => {
    if (!testTtsMessage.trim()) {
      return;
    }

    if (!user) {
      setTestTtsStatus('error');
      setTimeout(() => setTestTtsStatus('idle'), 3000);
      return;
    }

    setIsSendingTestTts(true);
    setTestTtsStatus('sending');

    try {
      // Always use SpongeBob voice for testing
      const voiceId = '54e3a85ac9594ffa83264b8a494b901b';

      // Call Supabase Edge Function (secure, server-side)
      const { data, error } = await supabase.functions.invoke('send-test-tts', {
        body: {
          text: testTtsMessage,
          voiceId: voiceId
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data.success) {
        // Update rate limit info if provided
        if (data.remaining !== undefined && data.limit !== undefined) {
          setRateLimitInfo({ remaining: data.remaining, limit: data.limit });
        }
        throw new Error(data.error || 'Failed to send test TTS');
      }

      // Update rate limit info
      if (data.remaining !== undefined && data.limit !== undefined) {
        setRateLimitInfo({ remaining: data.remaining, limit: data.limit });
      }

      setTestTtsStatus('success');
      setTimeout(() => {
        setTestTtsStatus('idle');
        setTestTtsMessage('');
      }, 2000);
    } catch (error) {
      console.error('Failed to send test TTS:', error);
      setTestTtsStatus('error');
      setTimeout(() => setTestTtsStatus('idle'), 3000);
    } finally {
      setIsSendingTestTts(false);
    }
  };

  if (!currentSettings) {
    return (
      <section id="extension-settings" className="content-section">
        <div className="flex justify-between items-center mb-6 header-border pb-4">
          <h2 className="text-xl font-semibold text-white">error</h2>
        </div>
        <p className="text-gray-400">no settings found for this extension.</p>
      </section>
    );
  }

  const renderField = (field: any) => {
    const value = formValues[field.id];

    switch (field.type) {
      case 'text':
        // Special handling for goals-overlay URL field
        if (field.id === 'overlay-url' && extensionId === 'goals-overlay') {
          const copyGoalsUrl = async () => {
            try {
              await navigator.clipboard.writeText(goalsOverlayUrl);
              setGoalsUrlCopied(true);
              setTimeout(() => setGoalsUrlCopied(false), 2000);
            } catch (error) {
              console.error('Failed to copy:', error);
            }
          };

          return (
            <div className="space-y-3">
              <label className="paint-label">{field.label}:</label>
              {!user ? (
                <div className="paint-input bg-gray-900 border-amber-500 text-amber-400">
                  <i className="fa-solid fa-lock mr-2"></i>
                  please sign in to view your goals overlay url
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={goalsOverlayUrl}
                      readOnly
                      className="paint-input flex-grow font-mono text-xs"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      type="button"
                      onClick={copyGoalsUrl}
                      className={`paint-button paint-button-sm ${
                        goalsUrlCopied ? 'bg-green-600 hover:bg-green-700' : ''
                      }`}
                      title="copy to clipboard"
                    >
                      <i className={`fa-solid ${goalsUrlCopied ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                      {goalsUrlCopied ? 'copied!' : 'copy'}
                    </button>
                  </div>
                  <div className="text-xs text-gray-400 space-y-1">
                    <p>
                      <i className="fa-solid fa-circle-info mr-1"></i>
                      add this as a browser source in obs (recommended size: 800x600)
                    </p>
                    <p className="text-amber-500 italic">
                      <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                      overlay auto-refreshes stats every minute
                    </p>
                  </div>
                </>
              )}
              {field.description && (
                <p className="text-xs text-gray-500 mt-1">{field.description}</p>
              )}
            </div>
          );
        }

        // Special handling for rumble-chat-monitor URL field
        if (field.id === 'overlay-url' && extensionId === 'rumble-chat-monitor') {
          const copyRumbleChatUrl = async () => {
            try {
              await navigator.clipboard.writeText(rumbleChatMonitorUrl);
              setRumbleChatUrlCopied(true);
              setTimeout(() => setRumbleChatUrlCopied(false), 2000);
            } catch (error) {
              console.error('Failed to copy:', error);
            }
          };

          return (
            <div className="space-y-3">
              <label className="paint-label">{field.label}:</label>
              {!user ? (
                <div className="paint-input bg-gray-900 border-amber-500 text-amber-400">
                  <i className="fa-solid fa-lock mr-2"></i>
                  please sign in to view your rumble chat monitor url
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={rumbleChatMonitorUrl}
                      readOnly
                      className="paint-input flex-grow font-mono text-xs"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      type="button"
                      onClick={copyRumbleChatUrl}
                      className={`paint-button paint-button-sm ${
                        rumbleChatUrlCopied ? 'bg-green-600 hover:bg-green-700' : ''
                      }`}
                      title="copy to clipboard"
                    >
                      <i className={`fa-solid ${rumbleChatUrlCopied ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                      {rumbleChatUrlCopied ? 'copied!' : 'copy'}
                    </button>
                  </div>
                  <div className="text-xs text-gray-400 space-y-1">
                    <p>
                      <i className="fa-solid fa-circle-info mr-1"></i>
                      add this as a browser source in obs (recommended size: 400x600)
                    </p>
                    <p className="text-amber-500 italic">
                      <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                      overlay updates automatically when configured stats change
                    </p>
                  </div>
                </>
              )}
              {field.description && (
                <p className="text-xs text-gray-500 mt-1">{field.description}</p>
              )}
            </div>
          );
        }

        // Special handling for social-media-rotator URL field
        if (field.id === 'overlay-url' && extensionId === 'social-media-rotator') {
          const copySocialUrl = async () => {
            try {
              await navigator.clipboard.writeText(socialMediaRotatorUrl);
              setSocialUrlCopied(true);
              setTimeout(() => setSocialUrlCopied(false), 2000);
            } catch (error) {
              console.error('Failed to copy:', error);
            }
          };

          return (
            <div className="space-y-3">
              <label className="paint-label">{field.label}:</label>
              {!user ? (
                <div className="paint-input bg-gray-900 border-amber-500 text-amber-400">
                  <i className="fa-solid fa-lock mr-2"></i>
                  please sign in to view your social media rotator url
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={socialMediaRotatorUrl}
                      readOnly
                      className="paint-input flex-grow font-mono text-xs"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      type="button"
                      onClick={copySocialUrl}
                      className={`paint-button paint-button-sm ${
                        socialUrlCopied ? 'bg-green-600 hover:bg-green-700' : ''
                      }`}
                      title="copy to clipboard"
                    >
                      <i className={`fa-solid ${socialUrlCopied ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                      {socialUrlCopied ? 'copied!' : 'copy'}
                    </button>
                  </div>
                  <div className="text-xs text-gray-400 space-y-1">
                    <p>
                      <i className="fa-solid fa-circle-info mr-1"></i>
                      add this as a browser source in obs (recommended size: 400x100)
                    </p>
                    <p className="text-amber-500 italic">
                      <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                      overlay updates when you change settings
                    </p>
                  </div>
                </>
              )}
              {field.description && (
                <p className="text-xs text-gray-500 mt-1">{field.description}</p>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-2">
            <label htmlFor={field.id} className="paint-label">
              {field.label}:
            </label>
            <input
              type={field.inputType || 'text'}
              id={field.id}
              name={field.id}
              value={value || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`paint-input ${field.disabled ? 'input-disabled' : ''}`}
              placeholder={field.placeholder || ''}
              disabled={field.disabled}
            />
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'text-with-save':
        // Special handling for rumble-chat-monitor preview
        if (field.id === 'overlay-preview' && extensionId === 'rumble-chat-monitor') {
          return (
            <div className="space-y-3">
              <label className="paint-label">{field.label}:</label>
              {!user ? (
                <div className="paint-input bg-gray-900 border-amber-500 text-amber-400">
                  <i className="fa-solid fa-lock mr-2"></i>
                  please sign in to preview your overlay
                </div>
              ) : (
                <div className="border-2 border-gray-700 rounded-lg overflow-hidden bg-gray-900">
                  <iframe
                    src={rumbleChatMonitorUrl}
                    className="w-full"
                    style={{ height: '400px', border: 'none' }}
                    title="Overlay Preview"
                  />
                </div>
              )}
              {field.description && (
                <p className="text-xs text-gray-500">{field.description}</p>
              )}
            </div>
          );
        }

        const hasChanged = value !== originalValues[field.id];
        return (
          <div className="space-y-2">
            <label htmlFor={field.id} className="paint-label">
              {field.label}:
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id={field.id}
                name={field.id}
                value={value || ''}
                onChange={(e) => handleInputChange(field.id, e.target.value)}
                className="paint-input flex-grow"
                placeholder={field.placeholder || ''}
              />
              {hasChanged && (
                <button
                  type="button"
                  onClick={handleSaveMentionWord}
                  disabled={mentionWordSaveStatus === 'saving'}
                  className={`paint-button paint-button-sm min-w-[80px] ${
                    mentionWordSaveStatus === 'success' ? 'bg-green-600 hover:bg-green-700' :
                    mentionWordSaveStatus === 'error' ? 'bg-red-600 hover:bg-red-700' :
                    ''
                  }`}
                >
                  {mentionWordSaveStatus === 'saving' ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                      saving...
                    </>
                  ) : mentionWordSaveStatus === 'success' ? (
                    <>
                      <i className="fa-solid fa-check mr-1"></i>
                      saved!
                    </>
                  ) : mentionWordSaveStatus === 'error' ? (
                    <>
                      <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                      error
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-save mr-1"></i>
                      save
                    </>
                  )}
                </button>
              )}
            </div>
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2">
            <label htmlFor={field.id} className="paint-label">
              {field.label}:
            </label>
            <textarea
              id={field.id}
              name={field.id}
              value={value || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className={`paint-textarea ${field.disabled ? 'input-disabled' : ''}`}
              placeholder={field.placeholder || ''}
              rows={field.rows || 3}
              disabled={field.disabled}
            />
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
            {/* Save button for personality field only */}
            {field.id === 'bot-personality' && (extensionId === 'chat-bot' || extensionId === 'chatbot') && (
              <button
                className={`paint-button paint-button-sm ${
                  saveStatus === 'success' ? 'bg-green-600 hover:bg-green-700' :
                  saveStatus === 'error' ? 'bg-red-600 hover:bg-red-700' :
                  ''
                }`}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                    saving...
                  </>
                ) : saveStatus === 'success' ? (
                  <>
                    <i className="fa-solid fa-check mr-1"></i>
                    saved!
                  </>
                ) : saveStatus === 'error' ? (
                  <>
                    <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                    error
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-save mr-1"></i>
                    save personality
                  </>
                )}
              </button>
            )}
          </div>
        );

      case 'number':
        return (
          <div className="space-y-2">
            <label htmlFor={field.id} className="paint-label">
              {field.label}:
            </label>
            <input 
              type="number"
              id={field.id}
              name={field.id}
              value={value || ''}
              onChange={(e) => handleInputChange(field.id, parseInt(e.target.value) || field.min || 0)}
              className="paint-input"
              placeholder={field.placeholder || ''}
              min={field.min}
              max={field.max}
            />
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'range':
        return (
          <div className="space-y-2">
            <label htmlFor={field.id} className="paint-label">
              {field.label}:
            </label>
            <div className="flex items-center gap-4">
              <input 
                type="range"
                id={field.id}
                name={field.id}
                value={value || field.default || field.min || 0}
                onChange={(e) => handleInputChange(field.id, parseInt(e.target.value))}
                className="flex-grow"
                min={field.min || 0}
                max={field.max || 100}
                step={field.step || 1}
              />
              <span className="text-white font-mono text-sm min-w-[3rem] text-right">
                {value || field.default || field.min || 0}
                {field.id.includes('chance') || field.id.includes('frequency') ? '%' : ''}
                {field.id.includes('delay') && field.max && field.max > 1000 ? 'ms' : ''}
              </span>
            </div>
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'select':
        return (
          <div className="space-y-2">
            <label htmlFor={field.id} className="paint-label">
              {field.label}:
            </label>
            <select
              id={field.id}
              name={field.id}
              value={value || field.default || ''}
              onChange={(e) => handleInputChange(field.id, e.target.value)}
              className="paint-select"
            >
              {field.options?.map((option: string | { value: string; label: string }) => {
                // Handle both string options and object options
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;

                return (
                  <option key={optionValue} value={optionValue}>
                    {optionLabel}
                  </option>
                );
              })}
            </select>
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'toggle':
        return (
          <div className="space-y-3">
            <label className="paint-label inline-flex items-center justify-between">
              <span>{field.label}</span>
              <label className="toggle-switch ml-4">
                <input
                  type="checkbox"
                  id={field.id}
                  checked={value !== undefined ? value : field.default}
                  onChange={async (e) => {
                    handleInputChange(field.id, e.target.checked);

                    // Auto-save for bot-enabled and tts-enabled toggles using dedicated columns
                    if (field.id === 'bot-enabled' && (extensionId === 'chat-bot' || extensionId === 'chatbot')) {
                      try {
                        await supabase.rpc('update_bot_enabled', { new_value: e.target.checked });
                      } catch (error) {
                        console.error('Failed to update bot-enabled:', error);
                      }
                    } else if (field.id === 'tts-enabled' && extensionId === 'tts-integration') {
                      try {
                        await supabase.rpc('update_tts_enabled', { new_value: e.target.checked });
                      } catch (error) {
                        console.error('Failed to update tts-enabled:', error);
                      }
                    }
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </label>
            {field.description && (
              <p className="text-xs text-gray-500">{field.description}</p>
            )}
          </div>
        );

      case 'segmented-control':
        // Filter options based on required extension
        const availableOptions = field.options?.filter((option: any) => {
          // If field requires tts-integration and it's not enabled, only show 'chat' option
          if (field.requiresExtension === 'tts-integration' && !ttsIntegrationEnabled) {
            return option.value === 'chat';
          }
          return true;
        }) || [];

        return (
          <div className="space-y-3">
            <label className="paint-label">{field.label}:</label>
            <div className="flex gap-2 p-1 bg-black border-2 border-white rounded">
              {availableOptions.map((option: any) => {
                const isSelected = (value || field.default) === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={async () => {
                      handleInputChange(field.id, option.value);
                      // Auto-save response mode
                      if (extensionId === 'chat-bot' || extensionId === 'chatbot') {
                        await updateExtensionSetting(extensionId, {
                          enabled: true, // Keep extension activated
                          [field.id]: option.value
                        });
                      }
                    }}
                    className={`flex-1 py-2 px-4 text-sm font-medium transition-colors rounded ${
                      isSelected
                        ? 'bg-white text-black'
                        : 'bg-transparent text-white hover:bg-white hover:bg-opacity-10'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
            {field.requiresExtension === 'tts-integration' && !ttsIntegrationEnabled && (
              <p className="text-xs text-amber-500 mt-1">
                <i className="fa-solid fa-info-circle mr-1"></i>
                Enable TTS Integration extension to use TTS response modes
              </p>
            )}
          </div>
        );

      case 'role-permissions':
        return (
          <div className="space-y-3">
            <label className="paint-label">{field.label}:</label>
            <div className="grid grid-cols-2 gap-3">
              {field.roleOptions?.map((role: string) => (
                <label key={role} className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={(value || field.default || []).includes(role)}
                    onChange={() => handleRoleToggle(field.id, role)}
                    className="paint-checkbox"
                  />
                  <span className="text-white capitalize">{role}</span>
                </label>
              ))}
            </div>
            {field.description && (
              <p className="text-xs text-gray-500">{field.description}</p>
            )}
          </div>
        );

      case 'personality-presets':
        return (
          <div className="space-y-3">
            <label className="paint-label">{field.label}:</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {field.presets?.map((preset: { name: string; value: string }) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => handleInputChange('bot-personality', preset.value.replace(/\\n/g, '\n'))}
                  className="paint-button paint-button-sm text-xs py-2 px-3 hover:bg-gray-700 transition-colors"
                >
                  {preset.name}
                </button>
              ))}
            </div>
            {field.description && (
              <p className="text-xs text-gray-500">{field.description}</p>
            )}
          </div>
        );

      case 'user-list':
        const userList = value || [];
        const newUser = newUserInputs[field.id] || '';
        
        return (
          <div className="space-y-3">
            <label htmlFor={field.id} className="paint-label">
              {field.label}:
            </label>
            <div className="flex gap-2">
              <input 
                type="text"
                value={newUser}
                onChange={(e) => setNewUserInputs(prev => ({ ...prev, [field.id]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddUser(field.id, newUser);
                    setNewUserInputs(prev => ({ ...prev, [field.id]: '' }));
                  }
                }}
                className="paint-input flex-grow"
                placeholder={field.placeholder || 'enter username'}
              />
              <button
                type="button"
                onClick={() => {
                  handleAddUser(field.id, newUser);
                  setNewUserInputs(prev => ({ ...prev, [field.id]: '' }));
                }}
                className="paint-button paint-button-sm"
                disabled={!newUser.trim()}
              >
                <i className="fa-solid fa-plus"></i>
              </button>
            </div>
            {userList.length > 0 && (
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {userList.map((username: string) => (
                  <div key={username} className="flex items-center justify-between bg-gray-900 p-2 rounded border border-gray-700">
                    <span className="text-white text-sm">{username}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(field.id, username)}
                      className="paint-button paint-button-icon paint-button-delete"
                      title="remove user"
                    >
                      <i className="fa-solid fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {field.description && (
              <p className="text-xs text-gray-500">{field.description}</p>
            )}
          </div>
        );

      case 'tts-overlay-url':
        const copyToClipboard = async () => {
          try {
            await navigator.clipboard.writeText(ttsOverlayUrl);
            setUrlCopied(true);
            setTimeout(() => setUrlCopied(false), 2000);
          } catch (error) {
            console.error('Failed to copy:', error);
          }
        };

        return (
          <div className="space-y-3">
            <label className="paint-label">{field.label}:</label>
            {isLoadingUrl ? (
              <div className="paint-input flex items-center gap-2 text-gray-400">
                <i className="fa-solid fa-spinner fa-spin"></i>
                loading your unique url...
              </div>
            ) : !user ? (
              <div className="paint-input bg-gray-900 border-amber-500 text-amber-400">
                <i className="fa-solid fa-lock mr-2"></i>
                please sign in to view your tts overlay url
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={ttsOverlayUrl}
                    readOnly
                    className="paint-input flex-grow font-mono text-xs"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className={`paint-button paint-button-sm ${
                      urlCopied ? 'bg-green-600 hover:bg-green-700' : ''
                    }`}
                    title="copy to clipboard"
                  >
                    <i className={`fa-solid ${urlCopied ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                    {urlCopied ? 'copied!' : 'copy'}
                  </button>
                </div>
                <div className="text-xs text-gray-400 space-y-1">
                  <p>
                    <i className="fa-solid fa-circle-info mr-1"></i>
                    add this as a browser source in obs
                  </p>
                  <p className="text-amber-500 italic">
                    <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                    keep this url private - it's unique to your account
                  </p>
                </div>
              </>
            )}
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'tts-test':
        const getStatusIcon = () => {
          switch (testTtsStatus) {
            case 'sending':
              return <i className="fa-solid fa-spinner fa-spin"></i>;
            case 'success':
              return <i className="fa-solid fa-check"></i>;
            case 'error':
              return <i className="fa-solid fa-exclamation-triangle"></i>;
            default:
              return <i className="fa-solid fa-volume-high"></i>;
          }
        };

        const getStatusText = () => {
          switch (testTtsStatus) {
            case 'sending':
              return 'sending...';
            case 'success':
              return 'sent!';
            case 'error':
              return 'failed';
            default:
              return 'test tts';
          }
        };

        const getButtonClass = () => {
          const baseClass = 'paint-button min-w-[120px]';
          switch (testTtsStatus) {
            case 'success':
              return `${baseClass} bg-green-600 hover:bg-green-700`;
            case 'error':
              return `${baseClass} bg-red-600 hover:bg-red-700`;
            default:
              return baseClass;
          }
        };

        return (
          <div className="space-y-3">
            <label className="paint-label">{field.label}:</label>
            {!user ? (
              <div className="paint-input bg-gray-900 border-amber-500 text-amber-400">
                <i className="fa-solid fa-lock mr-2"></i>
                please sign in to test tts
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={testTtsMessage}
                    onChange={(e) => setTestTtsMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isSendingTestTts) {
                        handleSendTestTts();
                      }
                    }}
                    className="paint-input flex-grow"
                    placeholder="type a test message..."
                    disabled={isSendingTestTts}
                  />
                  <button
                    type="button"
                    onClick={handleSendTestTts}
                    disabled={isSendingTestTts || !testTtsMessage.trim()}
                    className={getButtonClass()}
                    title="send test tts"
                  >
                    {getStatusIcon()}
                    <span className="ml-1">{getStatusText()}</span>
                  </button>
                </div>
                <div className="text-xs text-gray-400 space-y-1">
                  <p>
                    <i className="fa-solid fa-circle-info mr-1"></i>
                    send a test tts message to your obs overlay
                  </p>
                  {rateLimitInfo && (
                    <p className={rateLimitInfo.remaining === 0 ? 'text-amber-500' : 'text-gray-400'}>
                      <i className="fa-solid fa-gauge mr-1"></i>
                      {rateLimitInfo.remaining} / {rateLimitInfo.limit} tests remaining
                    </p>
                  )}
                  {testTtsStatus === 'error' && (
                    <p className="text-red-500">
                      <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                      failed to send test tts - check rate limit or make sure overlay is open
                    </p>
                  )}
                </div>
              </>
            )}
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'voice-list':
        return (
          <div className="space-y-2">
            <label className="paint-label">{field.label}:</label>
            {voices.length === 0 ? (
              <div className="paint-input flex items-center gap-2 text-gray-400">
                <i className="fa-solid fa-spinner fa-spin"></i>
                loading voices...
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {voices.map((voice) => (
                  <div
                    key={voice.id}
                    className="paint-card p-2 flex items-center gap-2"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-800 rounded flex-shrink-0">
                      <i className={`${voice.avatar} text-white text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-xs truncate">{voice.name}</p>
                      {voice.short_name && (
                        <code className="text-xs text-blue-400 font-mono">
                          !{voice.short_name}
                        </code>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'voice-cards':
        // Sort voices to put selected one first
        const sortedVoices = [...voices].sort((a, b) => {
          const aSelected = value === a.id || (!value && field.default === a.id);
          const bSelected = value === b.id || (!value && field.default === b.id);
          if (aSelected && !bSelected) return -1;
          if (!aSelected && bSelected) return 1;
          return 0;
        });

        return (
          <div className="space-y-3">
            <label className="paint-label">{field.label}:</label>
            {isLoadingVoices ? (
              <div className="flex items-center justify-center p-8 text-gray-400">
                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                loading voices...
              </div>
            ) : voices.length === 0 ? (
              <div className="flex items-center justify-center p-8 text-gray-400">
                <i className="fa-solid fa-exclamation-circle mr-2"></i>
                no voices available
              </div>
            ) : (
              <div className="overflow-x-auto pb-4">
                <div className="flex gap-4 min-w-max">
                  {sortedVoices.map((voice) => {
                    const isSelected = value === voice.id || (!value && field.default === voice.id);
                    return (
                      <button
                        key={voice.id}
                        type="button"
                        onClick={async () => {
                          handleInputChange(field.id, voice.id);
                          // Auto-save voice selection
                          if (extensionId === 'chat-bot' || extensionId === 'chatbot') {
                            const selectedVoice = voices.find(v => v.id === voice.id);
                            await saveChatbotSettings(
                              formValues['bot-personality'] || '',
                              voice.id,
                              selectedVoice?.name || ''
                            );
                          }
                        }}
                        className={`paint-card p-6 flex flex-col items-center justify-center gap-3 min-w-[160px] transition-all ${
                          isSelected
                            ? 'border-green-400 bg-green-400 bg-opacity-10 shadow-lg'
                            : 'border-gray-600 hover:border-white hover:bg-opacity-5'
                        }`}
                      >
                        <i className={`${voice.avatar} text-4xl ${isSelected ? 'text-green-400' : 'text-gray-400'}`}></i>
                        <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-gray-400'}`}>
                          {voice.name}
                        </span>
                        {isSelected && (
                          <i className="fa-solid fa-check-circle text-green-400 text-sm"></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {field.description && (
              <p className="text-xs text-gray-500 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'social-media-list':
        const socialList = value || [];
        const newSocial = newSocialInputs[field.id] || { platform: '', handle: '' };

        const PLATFORMS = [
          { value: 'twitter', label: 'Twitter', icon: '/twitter.svg' },
          { value: 'x', label: 'X', icon: '/x.svg' },
          { value: 'instagram', label: 'Instagram', icon: '/instagram.svg' },
          { value: 'tiktok', label: 'TikTok', icon: '/tiktok.svg' },
          { value: 'youtube', label: 'YouTube', icon: '/youtube.svg' },
          { value: 'twitch', label: 'Twitch', icon: '/twitch.svg' },
          { value: 'kick', label: 'Kick', icon: '/kick.svg' },
          { value: 'discord', label: 'Discord', icon: '/discord.svg' },
          { value: 'facebook', label: 'Facebook', icon: '/facebook.svg' },
          { value: 'snapchat', label: 'Snapchat', icon: '/snapchat.svg' },
          { value: 'onlyfans', label: 'OnlyFans', icon: '/onlyfans.svg' },
          { value: 'cashapp', label: 'CashApp', icon: '/cashapp.svg' }
        ];

        const handleAddSocial = async () => {
          if (newSocial.platform && newSocial.handle.trim()) {
            const updatedList = [...socialList, newSocial];
            setFormValues(prev => ({ ...prev, [field.id]: updatedList }));
            setNewSocialInputs(prev => ({ ...prev, [field.id]: { platform: '', handle: '' } }));

            // Auto-save for social-media-rotator
            if (extensionId === 'social-media-rotator') {
              try {
                await updateExtensionSetting(extensionId, {
                  ...formValues,
                  [field.id]: updatedList,
                  enabled: true
                });
              } catch (error) {
                console.error('Failed to auto-save social media link:', error);
              }
            }
          }
        };

        const handleRemoveSocial = async (index: number) => {
          const updated = socialList.filter((_: any, i: number) => i !== index);
          setFormValues(prev => ({ ...prev, [field.id]: updated }));

          // Auto-save for social-media-rotator
          if (extensionId === 'social-media-rotator') {
            try {
              await updateExtensionSetting(extensionId, {
                ...formValues,
                [field.id]: updated,
                enabled: true
              });
            } catch (error) {
              console.error('Failed to auto-save after removing social media link:', error);
            }
          }
        };

        return (
          <div className="space-y-3">
            <label className="paint-label">{field.label}:</label>

            <div className="space-y-2">
              <div className="flex gap-2">
                <select
                  value={newSocial.platform}
                  onChange={(e) => setNewSocialInputs(prev => ({
                    ...prev,
                    [field.id]: { ...newSocial, platform: e.target.value }
                  }))}
                  className="paint-select w-32"
                >
                  <option value="">Select Platform</option>
                  {PLATFORMS.map(platform => (
                    <option key={platform.value} value={platform.value}>
                      {platform.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newSocial.handle}
                  onChange={(e) => setNewSocialInputs(prev => ({
                    ...prev,
                    [field.id]: { ...newSocial, handle: e.target.value }
                  }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSocial();
                    }
                  }}
                  className="paint-input flex-grow"
                  placeholder="Enter handle (e.g., @username)"
                />
                <button
                  type="button"
                  onClick={handleAddSocial}
                  className="paint-button paint-button-sm"
                  disabled={!newSocial.platform || !newSocial.handle.trim()}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              </div>
            </div>

            {socialList.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {socialList.map((social: any, index: number) => {
                  const platform = PLATFORMS.find(p => p.value === social.platform);
                  return (
                    <div key={index} className="flex items-center gap-3 bg-gray-900 p-3 rounded border border-gray-700">
                      {platform && (
                        <img src={platform.icon} alt={platform.label} className="w-6 h-6" />
                      )}
                      <div className="flex-1">
                        <div className="text-white font-medium text-sm">{platform?.label || social.platform}</div>
                        <div className="text-gray-400 text-xs">{social.handle}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSocial(index)}
                        className="paint-button paint-button-icon paint-button-delete"
                        title="remove social"
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {field.description && (
              <p className="text-xs text-gray-500">{field.description}</p>
            )}
          </div>
        );

      case 'overlay-templates':
        const templates = [
          {
            id: 'full',
            name: 'Full Stats',
            description: 'Complete dashboard with all statistics',
            file: 'rumble-chat-monitor.html',
            size: '320x600px',
            icon: '📊'
          },
          {
            id: 'engagement',
            name: 'Engagement Only',
            description: 'Circular progress showing engagement rate',
            file: 'rumble-engagement-overlay.html',
            size: '300x400px',
            icon: '💯'
          },
          {
            id: 'viewers',
            name: 'Viewers Only',
            description: 'Large viewer count with peak and average',
            file: 'rumble-viewers-overlay.html',
            size: '350x300px',
            icon: '👁️'
          },
          {
            id: 'chatters',
            name: 'Chatters Only',
            description: 'Active chatters with message count',
            file: 'rumble-chatters-overlay.html',
            size: '350x300px',
            icon: '💬'
          },
          {
            id: 'combined',
            name: 'Combined Stats',
            description: 'Modern grid with key metrics',
            file: 'rumble-combined-overlay.html',
            size: '400x450px',
            icon: '📈'
          }
        ];

        const generateOverlayUrl = (templateFile: string) => {
          if (!user) return '';
          const baseUrl = window.location.origin;
          const topChatters = formValues['top-chatters-count'] || 20;
          const stats = (formValues['display-stats'] || []).join(',');
          const bgColor = encodeURIComponent(formValues['bg-color'] || '#121418');
          const textColor = encodeURIComponent(formValues['text-color'] || '#ffffff');
          const accentColor = encodeURIComponent(formValues['accent-color'] || '#85c742');

          let url = `${baseUrl}/${templateFile}?user=${user.id}&bgColor=${bgColor}&textColor=${textColor}&accentColor=${accentColor}`;

          if (templateFile === 'rumble-chat-monitor.html') {
            url += `&topChatters=${topChatters}&stats=${stats}`;
          }

          return url;
        };

        const copyTemplateUrl = async (url: string, templateId: string) => {
          try {
            await navigator.clipboard.writeText(url);
            setRumbleChatUrlCopied(true);
            setTimeout(() => setRumbleChatUrlCopied(false), 2000);
          } catch (error) {
            console.error('Failed to copy:', error);
          }
        };

        return (
          <div className="space-y-4">
            <label className="paint-label">{field.label}:</label>

            {!user ? (
              <div className="paint-input bg-gray-900 border-amber-500 text-amber-400">
                <i className="fa-solid fa-lock mr-2"></i>
                please sign in to view overlay templates
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {templates.map((template) => {
                  const templateUrl = generateOverlayUrl(template.file);

                  return (
                    <div key={template.id} className="border-2 border-gray-700 rounded-lg overflow-hidden bg-gray-900 hover:border-gray-600 transition-colors">
                      <div className="p-4 border-b border-gray-700 bg-gray-800">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">{template.icon}</span>
                            <div>
                              <h4 className="text-white font-semibold text-lg">{template.name}</h4>
                              <p className="text-gray-400 text-sm">{template.description}</p>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-900 px-2 py-1 rounded">
                            {template.size}
                          </span>
                        </div>
                      </div>

                      <div className="p-4">
                        <div className="mb-3">
                          <label className="text-xs text-gray-400 mb-1 block">OBS Browser Source URL:</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={templateUrl}
                              readOnly
                              className="paint-input flex-grow font-mono text-xs"
                              onClick={(e) => (e.target as HTMLInputElement).select()}
                            />
                            <button
                              type="button"
                              onClick={() => copyTemplateUrl(templateUrl, template.id)}
                              className={`paint-button paint-button-sm ${
                                rumbleChatUrlCopied ? 'bg-green-600 hover:bg-green-700' : ''
                              }`}
                              title="copy to clipboard"
                            >
                              <i className={`fa-solid ${rumbleChatUrlCopied ? 'fa-check' : 'fa-copy'}`}></i>
                            </button>
                          </div>
                        </div>

                        <div className="border-2 border-gray-700 rounded overflow-hidden bg-black">
                          <iframe
                            src={templateUrl}
                            className="w-full"
                            style={{
                              height: template.id === 'full' ? '400px' :
                                     template.id === 'engagement' ? '300px' :
                                     template.id === 'combined' ? '350px' : '250px',
                              border: 'none',
                              transform: 'scale(0.8)',
                              transformOrigin: 'top left',
                              width: '125%'
                            }}
                            title={`${template.name} Preview`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {field.description && (
              <p className="text-xs text-gray-500 mt-3">{field.description}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section id="extension-settings" className="content-section">
      <div className="flex justify-between items-center mb-6 header-border pb-4">
        <h2 id="extension-settings-title" className="text-xl font-semibold text-white">
          {currentSettings.name}
        </h2>
        {/* Hide save button for chat-bot, tts-integration, and social-media-rotator - they use auto-save or field-specific save */}
        {extensionId !== 'chat-bot' && extensionId !== 'chatbot' && extensionId !== 'tts-integration' && extensionId !== 'social-media-rotator' && (
          <button
            className={`paint-button paint-button-sm ${
              saveStatus === 'success' ? 'bg-green-600 hover:bg-green-700' :
              saveStatus === 'error' ? 'bg-red-600 hover:bg-red-700' :
              ''
            }`}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                saving...
              </>
            ) : saveStatus === 'success' ? (
              <>
                <i className="fa-solid fa-check mr-1"></i>
                saved!
              </>
            ) : saveStatus === 'error' ? (
              <>
                <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                error
              </>
          ) : (
            <>
              <i className="fa-solid fa-save mr-1"></i>
              save settings
            </>
          )}
          </button>
        )}
      </div>
      
      <div id="extension-form-container" className="space-y-8">
        {isLoadingSettings ? (
          // Loading skeleton
          <div className="space-y-6">
            <div className="border-2 border-dashed border-gray-600 p-6 space-y-6 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-1/4"></div>
              <div className="h-4 bg-gray-700 rounded w-1/2"></div>
            </div>
            <div className="border-2 border-dashed border-gray-600 p-6 space-y-6 animate-pulse">
              <div className="h-6 bg-gray-700 rounded w-1/3"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-700 rounded w-full"></div>
                <div className="h-4 bg-gray-700 rounded w-full"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        ) : currentSettings.sections ? (
          // Render sections with dotted borders
          <>
            {currentSettings.sections.map((section: ExtensionSection, index: number) => {
              // Hide "TTS Configuration" section when tts-enabled is false
              if (section.title === 'TTS Configuration' && !formValues['tts-enabled']) {
                return null;
              }

              // Hide chat-bot configuration sections when bot-enabled is false
              if (section.title !== 'Bot Status' && extensionId === 'chat-bot' && !formValues['bot-enabled']) {
                return null;
              }

              // Hide "Overlay URL" section for social-media-rotator since it's in the preview now
              if (extensionId === 'social-media-rotator' && section.title === 'Overlay URL') {
                return null;
              }

              const isTtsSection = section.title.toLowerCase().includes('tts');
              const ttsToggleField = isTtsSection ? section.fields.find(field => field.type === 'toggle') : null;
              const otherFields = isTtsSection ? section.fields.filter(field => field.type !== 'toggle') : section.fields;

              // Special handling for social-media-rotator Display Settings section - render with preview side by side
              if (extensionId === 'social-media-rotator' && section.title === 'Display Settings' && user && socialMediaRotatorUrl) {
                const copySocialUrl = async () => {
                  try {
                    await navigator.clipboard.writeText(socialMediaRotatorUrl);
                    setSocialUrlCopied(true);
                    setTimeout(() => setSocialUrlCopied(false), 2000);
                  } catch (error) {
                    console.error('Failed to copy:', error);
                  }
                };

                return (
                  <div key={section.title} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Display Settings */}
                    <div className="border-2 border-dashed border-gray-600 p-6 space-y-6">
                      <div className="space-y-2">
                        <h3 className="text-lg font-medium text-white">{section.title}</h3>
                        {section.description && (
                          <p className="text-sm text-gray-400">{section.description}</p>
                        )}
                      </div>

                      <div className="space-y-6">
                        {section.fields.map(field => (
                          <div key={field.id} className="space-y-2">
                            {renderField(field)}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Live Preview */}
                    <div className="border-2 border-dashed border-gray-600 p-6 space-y-4">
                      <div className="space-y-2">
                        <h3 className="text-lg font-medium text-white">live preview</h3>
                        <p className="text-sm text-gray-400">updates automatically as you change settings</p>
                      </div>

                      <div className="space-y-3">
                        <div
                          className="rounded border-2 border-gray-700 p-4 flex items-center justify-center"
                          style={{
                            minHeight: '150px',
                            backgroundColor: previewBgLight ? '#ffffff' : '#000000'
                          }}
                        >
                          <iframe
                            key={previewKey}
                            src={`${socialMediaRotatorUrl}&_t=${previewKey}`}
                            className="w-full"
                            style={{
                              height: '100px',
                              border: 'none',
                              background: 'transparent'
                            }}
                            title="social media rotator preview"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setPreviewBgLight(!previewBgLight)}
                            className="paint-button paint-button-sm flex-1"
                          >
                            <i className={`fa-solid ${previewBgLight ? 'fa-moon' : 'fa-sun'} mr-1`}></i>
                            {previewBgLight ? 'dark bg' : 'light bg'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setPreviewKey(prev => prev + 1)}
                            className="paint-button paint-button-sm flex-1"
                          >
                            <i className="fa-solid fa-rotate-right mr-1"></i>
                            refresh
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">
                          <i className="fa-solid fa-info-circle mr-1"></i>
                          toggle background to test contrast with dark fonts
                        </p>
                      </div>

                      {/* OBS URL Section */}
                      <div className="space-y-3 pt-3 border-t border-gray-700">
                        <label className="paint-label text-sm">obs browser source url:</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={socialMediaRotatorUrl}
                            readOnly
                            className="paint-input flex-grow font-mono text-xs"
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                          />
                          <button
                            type="button"
                            onClick={copySocialUrl}
                            className={`paint-button paint-button-sm ${
                              socialUrlCopied ? 'bg-green-600 hover:bg-green-700' : ''
                            }`}
                            title="copy to clipboard"
                          >
                            <i className={`fa-solid ${socialUrlCopied ? 'fa-check' : 'fa-copy'} mr-1`}></i>
                            {socialUrlCopied ? 'copied!' : 'copy'}
                          </button>
                        </div>
                        <div className="text-xs text-gray-400 space-y-1">
                          <p>
                            <i className="fa-solid fa-circle-info mr-1"></i>
                            add this as a browser source in obs (recommended size: 400x100)
                          </p>
                          <p className="text-amber-500 italic">
                            <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                            overlay updates automatically when you change settings
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={section.title} className="space-y-6">
                  <div className="border-2 border-dashed border-gray-600 p-6 space-y-6">
                    {isTtsSection && ttsToggleField ? (
                      // Special TTS section header with toggle (legacy support)
                      <>
                        <div className="flex justify-between items-center">
                          <div className="space-y-2">
                            <h3 className="text-lg font-medium text-white">{section.title}</h3>
                            {section.description && (
                              <p className="text-sm text-gray-400">{section.description}</p>
                            )}
                            {section.note && (
                              <p className="text-xs text-amber-500 italic font-light">{section.note}</p>
                            )}
                          </div>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              id={ttsToggleField.id}
                              checked={formValues[ttsToggleField.id] !== undefined ? formValues[ttsToggleField.id] : ttsToggleField.default}
                              onChange={async (e) => {
                                handleInputChange(ttsToggleField.id, e.target.checked);
                                setTtsExpanded(e.target.checked);

                                // Auto-save for tts-enabled toggle using dedicated column
                                if (ttsToggleField.id === 'tts-enabled' && extensionId === 'tts-integration') {
                                  try {
                                    await supabase.rpc('update_tts_enabled', { new_value: e.target.checked });
                                  } catch (error) {
                                    console.error('Failed to update tts-enabled:', error);
                                  }
                                }
                              }}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>

                        {/* Collapsible content */}
                        {(formValues['enable-tts'] || ttsExpanded) && (
                          <div className="space-y-6">
                            {otherFields.map(field => (
                              <div key={field.id} className="space-y-2">
                                {renderField(field)}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      // Regular section layout (including TTS without toggle)
                      <>
                        <div className="space-y-2">
                          <h3 className="text-lg font-medium text-white">{section.title}</h3>
                          {section.description && (
                            <p className="text-sm text-gray-400">{section.description}</p>
                          )}
                        </div>

                        <div className="space-y-6">
                          {section.fields.map(field => (
                            <div key={field.id} className="space-y-2">
                              {renderField(field)}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          // Fallback for old format without sections
          currentSettings.fields?.map(field => (
            <div key={field.id} className="space-y-2">
              {renderField(field)}
            </div>
          ))
        )}

        {/* Live Preview for Goals Overlay */}
        {extensionId === 'goals-overlay' && goalsOverlayUrl && user && (
          <div className="space-y-6 mt-8">
            <div className="border-2 border-dashed border-gray-600 p-6 space-y-4">
              <div className="flex justify-between items-center">
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-white">live preview</h3>
                  <p className="text-sm text-gray-400">see your overlay in real-time. save settings to update preview.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="paint-button paint-button-sm"
                >
                  <i className={`fa-solid ${showPreview ? 'fa-eye-slash' : 'fa-eye'} mr-1`}></i>
                  {showPreview ? 'hide preview' : 'show preview'}
                </button>
              </div>

              {showPreview && (
                <div className="space-y-3">
                  <div className="bg-black rounded border-2 border-gray-700 p-4 flex items-center justify-center" style={{ minHeight: '200px' }}>
                    <iframe
                      key={previewKey}
                      src={goalsOverlayUrl}
                      className="w-full"
                      style={{
                        height: formValues['layout'] === 'ticker' ? '80px' : '200px',
                        border: 'none',
                        background: 'transparent'
                      }}
                      title="goals overlay preview"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPreviewKey(prev => prev + 1)}
                      className="paint-button paint-button-sm"
                    >
                      <i className="fa-solid fa-rotate-right mr-1"></i>
                      refresh preview
                    </button>
                    <a
                      href={goalsOverlayUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="paint-button paint-button-sm"
                    >
                      <i className="fa-solid fa-external-link-alt mr-1"></i>
                      open in new tab
                    </a>
                  </div>
                  <p className="text-xs text-gray-500">
                    <i className="fa-solid fa-info-circle mr-1"></i>
                    preview auto-refreshes after saving settings. use "refresh preview" button to manually reload.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExtensionSettings;